import React, { useEffect, useState } from "react";
import { HiPaperClip } from "react-icons/hi";
import { FaUserCircle, FaPrint } from "react-icons/fa";
import { FiX, FiDownload, FiChevronLeft, FiChevronRight, FiCopy, FiCheck } from "react-icons/fi";
import TicketForm from "./TicketForm";
import api from "../utils/api";
import Loader from "../components/Loader";
import { getStatusLabel } from "../utils/statusStyles";

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
      style={{ lineHeight: 0, verticalAlign: 'middle', marginLeft: '6px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      className="text-gray-300 hover:text-gray-600 transition-colors cursor-pointer"
    >
      {copied
        ? <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700 }}>✓ Copied</span>
        : <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }
    </button>
  );
}

export default function TicketDetails({
  ticketId,
  onClose,
  priorityOptions = [],
  statusOptions = [],
  userInfo = {},
  selectedClientId = "",
  selectedTicketChildId = "",
  onSuccess,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = String(date.getFullYear());
    const h = String(date.getHours()).padStart(2, "0");
    const i = String(date.getMinutes()).padStart(2, "0");
    const s = String(date.getSeconds()).padStart(2, "0");
    return `${d}-${m}-${y} ${h}:${i}:${s}`;
  };

  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;

  useEffect(() => {
    const fetchTicketDetails = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/ticket/${ticketId}`);
        if (res.data?.status) {
          setTicket(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load ticket details", err);
      } finally {
        setLoading(false);
      }
    };
    if (ticketId) fetchTicketDetails();
  }, [ticketId]);

  const toCamelCase = (str) =>
    str
      ?.toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const handlePrint = () => {
    const originalContents = document.body.innerHTML;
    const printContents = document.getElementById("printableDiv").innerHTML;
    const printHeader = document.getElementById("printableDivHeader").innerHTML;

    // Concatenate header + content
    document.body.innerHTML = printHeader + printContents;
    window.print();
    // Restore original page
    document.body.innerHTML = originalContents;

    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 text-main">
        <Loader />
      </div>
    );
  }
  // if (loading || !ticket) return null;
  if (!ticket) return <div className="p-6 text-red-600">Ticket not found.</div>;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-40"
    >
      <div
        className="relative top-0 right-0 w-full md:w-[900px] h-full bg-white shadow-2xl border-l z-50 flex flex-col"
        onClick={(e) => e.stopPropagation()} // stop close on inside click
      >
        {/* Header */}
        <div
          id="printableDivHeader"
          className="flex items-center justify-between px-3 py-4 sticky top-0 z-10 bg-white"
        >
          <div className="flex-1 space-y-1 overflow-hidden">
            <h2 className="text-sm xs:text-xs sm:text-base md:text-lg lg:text-xl font-medium leading-5">
              #{ticket.ticket_master_id} - {ticket.title}
            </h2>

            <div className="flex flex-wrap gap-3 text-sm font-medium">
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Client: {ticket.client_name}
              </span>
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Module: {ticket.module_name}
              </span>
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Status: {(ticket.status_id >= 7 && ticket.status_id <= 11 && Number(userInfo?.role_id) === 2) ? "IN process" : getStatusLabel(ticket.status_id, ticket.status)}
              </span>
              <span className="border border-white/40 px-2 py-0.5 rounded">
                Reported By:{" "}
                {toCamelCase(ticket.comments?.slice(-1)[0]?.by || "-")}
              </span>
            </div>
          </div>

          {/* Buttons container */}
          <div className="absolute top-1 right-2 flex space-x-2 items-center">
            {/* Navigation Controls Removed from top */}

            {/* Print Button */}
            <button
              onClick={handlePrint}
              className="w-8 h-8 bg-black-600 text-black rounded-full flex items-center justify-center hover:text-white hover:bg-red-700 transition"
              aria-label="Print"
            >
              <FaPrint className="w-4 h-4" />
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
              aria-label="Close ticket details"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Update Form */}
          <div className="px-3 pb-4">
            <TicketForm
              isAdmin={false}
              onSuccess={onSuccess}
              onClose={onClose}
              priorityOptions={priorityOptions}
              statusOptions={statusOptions}
              userInfo={userInfo}
              selectedClientId={selectedClientId}
              selectedTicketId={ticketId}
              selectedStatusId={ticket.status_id}
              selectedPriorityId={ticket.priority_id}
              selectedTicketChildId={selectedTicketChildId}
              module_name={ticket?.module_name}
              selectedIssueType={ticket?.issue_type}
              ticket={ticket}
              onNext={onNext}
              onPrevious={onPrevious}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
            />
          </div>

          {/* Comments */}
          <div id="printableDiv" className="px-3 py-3 space-y-3">
            {(ticket.comments || [])
              .filter((c) => {
                if (userInfo?.role_id == 2) {
                  // Only show comments from Roles 1 (Vertical Head/Admin), 3 (Client Advocate), and 2 (Customer)
                  // AND hide internal testing process comments (Statuses 8, 9, 10, 11)
                  const isTestingStatus = [8, 9, 10, 11].includes(Number(c.status_id));
                  return [1, 2, 3].includes(Number(c.user_role)) && !isTestingStatus;
                }
                return true;
              })
              .map((c) => (
              <div
                key={c.comment_id}
                className="rounded-xl border border-gray-200 bg-white shadow-md p-4 relative overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="flex justify-between flex-wrap sm:flex-nowrap sm:justify-between sm:items-start">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full overflow-hidden">
                      <img
                        src={
                          c?.user_img
                            ? `${IMAGE_BASE_URL}/${c.user_img}`
                            : `${IMAGE_BASE_URL}/uploads/no_image.png` // fallback image
                        }
                        alt="Profile"
                        className="h-full w-full object-cover rounded-full"
                      />
                    </span>

                    <div>
                      <p className="text-sm font-semibold text-main">
                        {toCamelCase(c.by)}
                      </p>
                      <p className="text-xs text-main">{formatDate(c.on)}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-sm mt-2 sm:mt-0">
                    <span
                      className={`px-2 py-1 rounded border text-xs font-semibold ${
                        c.status === "Reopen"
                          ? "border-red-500 text-red-600"
                          : c.status === "Closed"
                          ? "border-gray-500 text-gray-600"
                          : c.status === "Pending"
                          ? "border-yellow-500 text-yellow-600"
                          : "border-blue-500 text-blue-600"
                      } bg-white`}
                    >
                      Status: {c.status == "Reopen" ? "Reopened" : c.status}
                    </span>
                    <span
                      className={`px-2 py-1 rounded border text-xs font-semibold bg-white ${
                        c.priority === "Critical"
                          ? "border-red-700 text-red-700"
                          : c.priority === "High"
                          ? "border-orange-500 text-orange-500"
                          : c.priority === "Medium"
                          ? "border-yellow-400 text-yellow-500"
                          : "border-green-500 text-green-500"
                      }`}
                    >
                      Priority: {c.priority}
                    </span>
                    <span className="px-2 py-1 rounded border border-emerald-600 text-emerald-600 bg-white text-xs font-semibold">
                      Assigned To: {c.assignee}
                    </span>
                  </div>
                </div>

                  <>
                    {c.descriptionss && (
                      <div className="mt-3 text-sm leading-relaxed break-words whitespace-pre-wrap overflow-x-auto w-full">
                        <span className="block break-all" dangerouslySetInnerHTML={{ __html: c.descriptionss }} />
                        <CopyButton text={c.descriptionss.replace(/<[^>]*>/g, '').trim()} />
                      </div>
                    )}

                    {c.attachments?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-600 flex items-center gap-2 mb-1">
                          <HiPaperClip /> Attachments:
                        </p>
                        <ul className="space-y-1 text-sm text-main">
                          {c.attachments.map((file) => {
                            const fileName = file.filepath.split("/").pop();
                            return (
                              <li
                                key={file.file_id}
                                className="list-disc sm:pl-5 ml-4"
                              >
                                <button
                                  onClick={() => {
                                    const files = c.attachments;
                                    const currentIndex = files.findIndex(
                                      (f) => f.filepath === file.filepath
                                    );
                                    setPreviewImages(files);
                                    setActiveImageIndex(currentIndex);
                                  }}
                                  className="hover:underline text-left w-full"
                                >
                                  {fileName}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
              </div>
            ))}
          </div>

          {/* Image Preview */}
          {previewImages.length > 0 && activeImageIndex !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 px-4">
              <div className="relative bg-white p-4 rounded shadow-lg w-full max-w-[600px] h-auto max-h-[70vh] flex flex-col items-center">
                <button
                  onClick={() => {
                    setPreviewImages([]);
                    setActiveImageIndex(null);
                  }}
                  className="absolute top-2 right-3 text-2xl text-gray-600 hover:text-black"
                >
                  <FiX />
                </button>

                {(() => {
                  const file = previewImages[activeImageIndex];
                  const fileUrl = `${process.env.REACT_APP_IMAGEURL}${file.filepath}`;
                  const fileName = file.filepath.split("/").pop();

                  return (
                    <div className="w-full flex flex-col items-center">
                      <p className="mb-2 text-gray-700 text-sm truncate w-full text-center">
                        {fileName}
                      </p>

                      <div className="w-full h-[40vh] flex items-center justify-center bg-gray-50 rounded border">
                        {file.filetype?.startsWith("image/") ? (
                          <img
                            src={fileUrl}
                            alt="Preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center">
                            <p className="text-gray-400 text-sm">
                              No preview available
                            </p>
                            <a
                              href={fileUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded bg-gray-100 hover:bg-gray-200 mt-2"
                              title="Download"
                            >
                              <FiDownload className="text-lg text-main" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-between items-center w-full mt-4">
                  <button
                    onClick={() =>
                      setActiveImageIndex((prev) => Math.max(0, prev - 1))
                    }
                    disabled={activeImageIndex === 0}
                    className={`text-sm px-3 py-1 rounded flex items-center gap-1
                    ${
                      activeImageIndex === 0
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-main text-white hover:bg-gray-700"
                    }`}
                  >
                    Previous
                  </button>

                  <span className="text-sm text-gray-500">
                    {activeImageIndex + 1} of {previewImages.length}
                  </span>

                  <button
                    onClick={() =>
                      setActiveImageIndex((prev) =>
                        Math.min(previewImages.length - 1, prev + 1)
                      )
                    }
                    disabled={activeImageIndex === previewImages.length - 1}
                    className={`text-sm px-3 py-1 rounded flex items-center gap-1
                    ${
                      activeImageIndex === previewImages.length - 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-main text-white hover:bg-gray-700"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
